import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelDynamic as _vModelDynamic, withKeys as _withKeys } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "editable-component__read-view"
}
const _hoisted_2 = {
  key: 1,
  class: "editable-component__read-view"
}
const _hoisted_3 = { class: "editable-component__placeholder" }
const _hoisted_4 = {
  key: 2,
  class: "editable-component__write-view"
}
const _hoisted_5 = ["value"]
const _hoisted_6 = ["type", "pattern", "maxlength", "max", "min"]

import moment from 'moment';
import Toast from '../services/toast';
import { RegexType } from '@/features/bots/models/designer/regexType';
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue';
import { MAX_INTEGER } from '../constants';
import { PropType, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useVModel } from '@vueuse/core';


export default /*@__PURE__*/_defineComponent({
  __name: 'EditableComponent',
  props: {
  modelValue: {
    type: [String, Number, Date],
    default: '',
  },
  fieldName: {
    type: String,
  },
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: 'unknown',
  },
  options: {
    type: Array as PropType<string[]>,
  },
  displayRelativeDate: {
    type: Boolean,
    default: true,
  },
  displayIcon: {
    type: Boolean,
    default: false,
  },
},
  emits: ["update:modelValue"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;
const value = useVModel(props, 'modelValue', emit);

const inputField = ref<HTMLInputElement | null>(null);
const pattern = ref('.*');
const maxLength = ref(100);
const max = ref(Infinity);
const min = ref(Infinity);
const actualValue = ref<any>('');
const valueToDisplay = ref<any>('');
const isEditing = ref(false);

const updateDateTimer = ref<number>();

onMounted(() => {
  if (props.type === 'tel') {
    pattern.value = RegexType.PHONE_NUMBER;
    maxLength.value = 14;
  } else if (props.type === 'number') {
    max.value = MAX_INTEGER;
    min.value = -MAX_INTEGER;
  } else if (props.type === 'decimal') {
    pattern.value = RegexType.DECIMAL;
    maxLength.value = 28;
  }
});

onBeforeUnmount(() => clearUpdateTimer());

watch(
  value,
  (newValue: any) => {
    if (!newValue) {
      valueToDisplay.value = '';
      actualValue.value = '';
    } else if (props.type === 'datetime-local') {
      setupDateTime(newValue);
    } else if (props.type === 'date') {
      if (newValue instanceof Date) {
        actualValue.value = moment(newValue).format('YYYY-MM-DD');
        valueToDisplay.value = getDateToDisplay();
        setUpdateDateTimer();
      } else {
        actualValue.value = String(newValue); // Fallback to string
        valueToDisplay.value = String(newValue);
      }
    } else {
      actualValue.value = String(newValue);
      valueToDisplay.value = String(newValue);
    }
  },
  { immediate: true },
);

function startEditing() {
  isEditing.value = true;

  nextTick(() => {
    if (inputField.value) {
      inputField.value.focus();
    }
  });
}

function finishEditing() {
  if (!inputField.value) {
    isEditing.value = false; // Exit editing mode safely
    return;
  }

  if (!inputField.value.checkValidity()) {
    Toast.showError(getErrorMessage());
    actualValue.value = value.value; // Reset to original value
    isEditing.value = false;
    return;
  }

  isEditing.value = false;
  if (props.type === 'datetime-local' || props.type === 'date') {
    value.value = actualValue.value ? new Date(actualValue.value) : null;
  } else if (props.type === 'number') {
    value.value = actualValue.value ? Number(actualValue.value) : null;
  } else {
    value.value = actualValue.value; // Keep as string for text, tel, etc.
  }
}

function setupDateTime(value: any) {
  if (value instanceof Date) {
    valueToDisplay.value = getDateToDisplay();
    setActualValueFromDate(value);
    if (props.displayRelativeDate) {
      setUpdateDateTimer();
    }
  }
}

function setActualValueFromDate(date: Date) {
  const dateAsIso = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
  if (dateHasSeconds(dateAsIso)) {
    actualValue.value = dateAsIso.substring(0, dateAsIso.lastIndexOf(':'));
  } else {
    actualValue.value = dateAsIso.substring(0, dateAsIso.length - 1); // remove zulu time from string
  }
}

function setUpdateDateTimer() {
  clearUpdateTimer();
  updateDateTimer.value = window.setInterval(
    () => (valueToDisplay.value = formatRelativeDate(actualValue.value)),
    30000,
  );
}

function clearUpdateTimer() {
  if (updateDateTimer.value) {
    window.clearInterval(updateDateTimer.value);
  }
}

function dateHasSeconds(value: string): boolean {
  return (value.match(/:/g) || []).length === 2;
}

function getErrorMessage() {
  if (props.fieldName) {
    return `${props.fieldName} is in an invalid format`;
  } else {
    return `${props.type} is in an invalid format`;
  }
}

function getDateToDisplay() {
  if (props.displayRelativeDate) {
    return formatRelativeDate(value.value);
  }
  return formatDate(value.value);
}

function formatRelativeDate(date: Date): string {
  if (!date) {
    return '';
  }
  return moment(date).fromNow();
}

function formatDate(date: Date) {
  return moment(date).format('DD/MM/YYYY HH:mm');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "editable-component",
    onClick: startEditing
  }, [
    (valueToDisplay.value && !isEditing.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("span", null, _toDisplayString(valueToDisplay.value), 1),
          (__props.displayIcon)
            ? (_openBlock(), _createBlock(EditIcon, { key: 0 }))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (!actualValue.value && !isEditing.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(__props.placeholder), 1),
          (__props.displayIcon)
            ? (_openBlock(), _createBlock(EditIcon, { key: 0 }))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (isEditing.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (__props.type === 'select')
            ? _withDirectives((_openBlock(), _createElementBlock("select", {
                key: 0,
                id: "select-type",
                ref_key: "inputField",
                ref: inputField,
                class: "select-type",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((actualValue).value = $event)),
                onBlur: finishEditing,
                onChange: finishEditing
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option) => {
                  return (_openBlock(), _createElementBlock("option", {
                    value: option,
                    key: option
                  }, _toDisplayString(option), 9, _hoisted_5))
                }), 128))
              ], 544)), [
                [_vModelSelect, actualValue.value]
              ])
            : _withDirectives((_openBlock(), _createElementBlock("input", {
                key: 1,
                ref_key: "inputField",
                ref: inputField,
                id: "edit-field",
                type: __props.type,
                pattern: pattern.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((actualValue).value = $event)),
                maxlength: maxLength.value,
                placeholder: "unknown",
                onBlur: finishEditing,
                onKeypress: _withKeys(finishEditing, ["enter"]),
                max: max.value,
                min: min.value
              }, null, 40, _hoisted_6)), [
                [_vModelDynamic, actualValue.value]
              ]),
          _cache[2] || (_cache[2] = _createElementVNode("label", {
            for: "select-type",
            class: "sr-only"
          }, "Edit Contact Type value", -1)),
          _cache[3] || (_cache[3] = _createElementVNode("label", {
            for: "edit-field",
            class: "sr-only"
          }, "Edit field value", -1))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})